import { usesMetricMeasurementSystem } from "../../configs/regionalProfile";

const CM_PER_INCH = 2.54;
const LB_PER_KG = 2.2046226218;

export const MIN_HEIGHT_CM = 100;
export const MAX_HEIGHT_CM = 220;
export const MIN_WEIGHT_KG = 30;
export const MAX_WEIGHT_KG = 300;

export const MIN_HEIGHT_FEET = 3;
export const MAX_HEIGHT_FEET = 7;
export const MIN_WEIGHT_LBS = Number((MIN_WEIGHT_KG * LB_PER_KG).toFixed(1));
export const MAX_WEIGHT_LBS = Number((MAX_WEIGHT_KG * LB_PER_KG).toFixed(1));

/**
 * Which units the UI shows. v92 read this off the UI locale (`locale === "ja"`),
 * which tied Japanese-the-language to metric-the-unit-system and would have
 * given a Canadian French or Indian English build imperial units. It now comes
 * from the configured regional profile.
 *
 * Metric only when the active regional profile says so. Storage and the API
 * remain cm/kg everywhere; only the displayed units change. Any locale
 * argument a caller still passes is ignored.
 */
export const usesMetricMeasurements = () => usesMetricMeasurementSystem();

export const centimetersToFeetAndInches = (centimeters) => {
  const value = Number(centimeters);
  if (!Number.isFinite(value) || value <= 0) {
    return { feet: "", inches: "" };
  }
  const totalInches = Math.round(value / CM_PER_INCH);
  return {
    feet: String(Math.floor(totalInches / 12)),
    inches: String(totalInches % 12),
  };
};

export const feetAndInchesToCentimeters = (feet, inches) => {
  const feetValue = Number(feet);
  const inchesValue = Number(inches);
  if (
    !Number.isFinite(feetValue) ||
    !Number.isFinite(inchesValue) ||
    feetValue < 0 ||
    inchesValue < 0 ||
    inchesValue >= 12 ||
    feetValue * 12 + inchesValue <= 0
  ) {
    return null;
  }
  return Number(((feetValue * 12 + inchesValue) * CM_PER_INCH).toFixed(1));
};

export const isHeightCentimetersInRange = (centimeters) => {
  const value = Number(centimeters);
  return (
    Number.isFinite(value) && value >= MIN_HEIGHT_CM && value <= MAX_HEIGHT_CM
  );
};

export const isWeightKilogramsInRange = (kilograms) => {
  const value = Number(kilograms);
  return (
    Number.isFinite(value) && value >= MIN_WEIGHT_KG && value <= MAX_WEIGHT_KG
  );
};

export const isWeightPoundsInRange = (pounds) => {
  const value = Number(pounds);
  return (
    Number.isFinite(value) && value >= MIN_WEIGHT_LBS && value <= MAX_WEIGHT_LBS
  );
};

export const kilogramsToPounds = (kilograms) => {
  const value = Number(kilograms);
  if (!Number.isFinite(value) || value <= 0) return "";
  return String(Number((value * LB_PER_KG).toFixed(1)));
};

export const poundsToKilograms = (pounds) => {
  const value = Number(pounds);
  if (!Number.isFinite(value) || value <= 0) return null;
  // Keep enough precision for a pounds → kilograms → pounds round trip.
  // Rounding the API value to one decimal changed entries such as 135 lb
  // into 134.9 lb when the edit form was opened again.
  return Number((value / LB_PER_KG).toFixed(3));
};
